import { useCallback, useEffect, useState } from 'react';
import { GUEST_AUTHENTICATED_EVENT } from '../utils/guestAuthRedirect';
import {
  confirmGuestUploadNotice, currentGuestUploadNotice, GUEST_UPLOAD_NOTICE_ACKNOWLEDGED,
  ensureGuestUploadAcknowledgement, GUEST_UPLOAD_NOTICE_CHANGED,
  readGuestUploadAcknowledgement, syncGuestUploadNotice,
} from '../utils/guestUploadNotice';
import { getGuestAuthToken } from '../utils/guestToken';

export function useGuestUploadNotice(galleryId, isPreview = false) {
  const [, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  const state = currentGuestUploadNotice(galleryId, isPreview);
  const version = state.active?.guestNoticeVersion;
  const sessionId = state.session?.sessionId;
  useEffect(() => {
    window.addEventListener(GUEST_AUTHENTICATED_EVENT, refresh);
    window.addEventListener(GUEST_UPLOAD_NOTICE_ACKNOWLEDGED, refresh);
    window.addEventListener(GUEST_UPLOAD_NOTICE_CHANGED, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(GUEST_AUTHENTICATED_EVENT, refresh);
      window.removeEventListener(GUEST_UPLOAD_NOTICE_ACKNOWLEDGED, refresh);
      window.removeEventListener(GUEST_UPLOAD_NOTICE_CHANGED, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [refresh]);
  useEffect(() => {
    if (!sessionId || isPreview) return;
    let cancelled = false;
    const sync = () => {
      if (navigator.onLine === false || document.visibilityState === 'hidden') return;
      const token = getGuestAuthToken();
      syncGuestUploadNotice(galleryId, token).then(() => {
        if (cancelled) return;
        refresh();
        const current = currentGuestUploadNotice(galleryId, isPreview);
        const local = current.required && readGuestUploadAcknowledgement(galleryId, current.active.guestNoticeVersion, token);
        if (local && !local.serverRecorded) return ensureGuestUploadAcknowledgement(galleryId, token, local);
      }).catch(() => { /* Offline or unavailable: the guest can still explicitly confirm locally. */ });
    };
    sync();
    window.addEventListener('online', sync);
    window.addEventListener('focus', sync);
    document.addEventListener('visibilitychange', sync);
    const interval = window.setInterval(sync, 60_000);
    return () => {
      cancelled = true;
      window.removeEventListener('online', sync);
      window.removeEventListener('focus', sync);
      document.removeEventListener('visibilitychange', sync);
      window.clearInterval(interval);
    };
  }, [galleryId, isPreview, refresh, sessionId]);
  return {
    ...state,
    canUpload() {
      const current = currentGuestUploadNotice(galleryId, isPreview);
      if (current.required && !current.acknowledged) { refresh(); return false; }
      return true;
    },
    confirm() {
      try {
        const record = confirmGuestUploadNotice(galleryId, { version, sessionId });
        if (navigator.onLine !== false) {
          ensureGuestUploadAcknowledgement(galleryId, getGuestAuthToken(), record).catch(() => {
            // The genuine local confirmation remains available offline. The upload queue
            // must deliver it successfully before it can obtain a signed upload URL.
          });
        }
      } finally { refresh(); }
    },
  };
}
